import { IconPlus, IconX } from "@tabler/icons-react";
import { Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Input } from "../../../../components/common/input";
import { IconPill } from "../../../../components/common/icon-pill";
import { SectionCard } from "../../../../components/common/section-card";
import { DisplayField } from "./display-field";

export interface WarrantyDisplayRow {
  id: number;
  period: string;
  type: string;
  provider: string;
}

export type NewWarrantyInput = Omit<WarrantyDisplayRow, "id">;

interface Props {
  warranties: WarrantyDisplayRow[];
  newWarranty: NewWarrantyInput | null;
  onWarrantyRemove: (id: number) => void;
  onNewWarrantyChange: (patch: Partial<NewWarrantyInput>) => void;
  onNewWarrantyStart: () => void;
  onNewWarrantySave: () => void;
  onNewWarrantyCancel: () => void;
  /** When true, hides remove icons + "Add warranty option" + the inline form. */
  readOnly?: boolean;
}

export function DetailsWarrantySection({
  warranties, newWarranty,
  onWarrantyRemove, onNewWarrantyChange, onNewWarrantyStart, onNewWarrantySave, onNewWarrantyCancel,
  readOnly = false,
}: Props) {
  return (
    <SectionCard shadow title="Warranty information" className="gap-5">
      <div className="flex flex-col gap-4">
        {warranties.map((w, i) => (
          <div key={w.id} className="flex flex-col gap-4 rounded-xl border border-borderDefault bg-bgLight p-4">
            <div className="flex items-center justify-between">
              <Text variant="bodySm" tone="dark">Warranty {i + 1}</Text>
              {!readOnly && (
                <IconPill
                  as="button"
                  size={24}
                  tone="ghost"
                  aria-label="Remove warranty"
                  onClick={() => onWarrantyRemove(w.id)}
                  className="!bg-transparent !text-textMuted hover:!text-textDark"
                >
                  <IconX size={16} />
                </IconPill>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4 max-md:grid-cols-1">
              <DisplayField label="Warranty period"   value={w.period} />
              <DisplayField label="Warranty type"     value={w.type} />
              <DisplayField label="Warranty provider" value={w.provider} />
            </div>
          </div>
        ))}

        {!readOnly && newWarranty && (
          <div className="flex flex-col gap-4 rounded-xl border border-borderDefault bg-bgLight p-4">
            <Text variant="bodySm" tone="dark">New warranty</Text>
            <div className="grid grid-cols-3 gap-4 max-md:grid-cols-1">
              <Input label="Warranty period"   value={newWarranty.period}   placeholder="e.g. 1 year"            onChange={(e) => onNewWarrantyChange({ period:   e.target.value })} />
              <Input label="Warranty type"     value={newWarranty.type}     placeholder="e.g. Carry-in"          onChange={(e) => onNewWarrantyChange({ type:     e.target.value })} />
              <Input label="Warranty provider" value={newWarranty.provider} placeholder="e.g. Merchant warranty" onChange={(e) => onNewWarrantyChange({ provider: e.target.value })} />
            </div>
            <div className="flex gap-3">
              <Button variant="primary"   size="sm" onClick={onNewWarrantySave}>Save</Button>
              <Button variant="secondary" size="sm" onClick={onNewWarrantyCancel}>Cancel</Button>
            </div>
          </div>
        )}

        {!readOnly && !newWarranty && (
          <Button
            variant="secondaryDark"
            size="sm"
            leftIcon={<IconPlus size={16} />}
            className="self-start !px-5 !py-2.5"
            onClick={onNewWarrantyStart}
          >
            Add warranty option
          </Button>
        )}
      </div>
    </SectionCard>
  );
}
